import { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { getUniqueID } from "../../../../utils/numbers";
import { useFormContext } from "react-hook-form";
import { getIsBlockedActions } from "../../../../actions/getters";
import { generateAdsCampaignDescription } from "../../../../actions/adsCampaigns";
import {
  ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION,
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ALERT_ICON_TYPE_SUCCESS,
  TIKTOK,
  ADS_CAMPAIGN_MEDIA_FROM_TIKTOK_POST,
  ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
  ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_TIKTOK,
  LABELS_TRACKING_ID,
  ADS_TIKTOK_CAMPAIGN_OBJECTIVES_VALID_WITH_IMAGES,
  MIN_WIDTH_SCREEN_TO_SHOW_CHAT,
  ADS_CAMPAIGN_STATUS_DRAFT,
  ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION,
} from "../../../../utils/constants";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import {
  ADS_CAMPAIGN_PREVIEW,
  ADS_CONFIGURATION_DESTINATION_TITLE,
  CALL_TO_ACTION_PLACEHOLDER,
  CALL_TO_ACTIONS_PLACEHOLDER,
  COPY_VALUE_IN_OTHER_ADS,
  DEEP_LINK_PLACEHOLDER,
  DESTINATION_URL_PLACEHOLDER,
  FIELD_MAX_LENGTH,
  FIELD_REQUIRED,
  FIELD_TEXT_REPEATED,
  FIELD_URL_VALID,
  OFFLINE_EVENTS_PLACEHOLDER,
  READY,
  TITLE,
  TITLE_PLACEHOLDER,
  TRACKING_EVENTS,
  UTM_PARAMETERS_PLACEHOLDER,
  INDIVIDUAL,
  CAROUSEL,
  CREATIVE_TYPE_PLACEHOLDER,
  CAROUSEL_IMAGES_MANAGER_TITLE,
  DARK_POST_PLACEHOLDER,
  DARK_POST_DESCRIPTION_PLACEHOLDER,
  COPY_VALUES_IN_OTHER_ADS,
} from "../../../../i18n/keysTranslations";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import { useDispatchApp } from "../../../../lib/redux";
import { useTranslationApp } from "../../../../lib/i18next";
import TextField from "../../../Form/TextField";
import { CardContent, useMediaQuery } from "@mui/material";
import CarouselImagesManager from "./CarouselImagesManager";
import AdPreviewTiktok from "./AdPreviewTiktok";
import { waitDelay } from "../../../../utils/date";
import { REGEX_URL } from "../../../../utils/regex";
import ClickableText from "../../../Texts/ClickableText";
import { SimpleAlert } from "../../../Alerts/Alerts";
import SelectCallToActionTikTok from "./SelectCallToActionTikTok";
import SelectTrackingIDField from "../../../Form/SelectTrackingIDField";
import ChipOptional from "../../../Chip/ChipOptional";
import SelectOfflineEventsTikTok from "./SelectOfflineEventsTikTok";
import CardContainer from "../../../Card/CardContainer";
import CardContainerWithCopy from "../../../Card/CardContainerWithCopy";
import TextFieldWithActions from "../../../Form/TextFieldWithActions";
import Select from "../../../Form/Select";
import MusicUploader from "../../../Form/MusicUploader";
import Switch from "../../../Form/Switch";
import UtmParametersContainer from "../../../Common/Utms/UtmParametersContainer";
import AdCatalogTikTokForm from "./AdCatalogTikTokForm";

const platform = TIKTOK;
const maxTitles = 1;

const AdTikTokForm = () => {
  const [isLoading, setIsLoading] = useState(true);
  const refPreview = useRef(null);

  const {
    watch,
    getValues,
    control,
    formState,
    sectionSelected,
    isGeneralDisabledFields,
    setValue,
    trigger,
    isChatVisible,
  } = useFormContext();
  useFocusErrorForm(formState);

  const errors = formState.errors;
  const adsGroups = watch("adsGroups");

  const adGroupID = sectionSelected.adGroupID;
  const indexAdGroup = adsGroups.findIndex((item) => item.id === adGroupID);
  const adGroup = adsGroups[indexAdGroup];
  const BASIC_PATH_AD_GROUP = `adsGroups.[${indexAdGroup}]`;

  const adID = sectionSelected.adID;
  const indexAd = adGroup?.ads?.findIndex((item) => item.id === adID);
  const BASIC_PATH = `adsGroups.[${indexAdGroup}].ads.[${indexAd}]`;
  const isIncompatibleAd = getValues(`${BASIC_PATH}.isIncompatible`) || false;
  const status = watch(`${BASIC_PATH}.status`) || ADS_CAMPAIGN_STATUS_DRAFT;
  const isAdCreated = status !== ADS_CAMPAIGN_STATUS_DRAFT;

  const isCarousel = watch(`${BASIC_PATH}.isCarousel`);

  const platformData = watch(`platformsData.${platform}`);
  const objective = platformData?.objective;
  const campaignType = platformData?.type;

  const isCatalogCampaignTikTok =
    objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION &&
    campaignType === ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION;

  const isDisabledFields =
    isGeneralDisabledFields ||
    platformData?.isIncompatible ||
    adGroup?.isIncompatible ||
    isIncompatibleAd;

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const { ADS_CAMPAIGN_GENERATE_TEXT_ACTION } = useCreditActionCosts();
  const breakColumnFormAndPreview = useMediaQuery(() => "(max-width: 1080px)");
  const isLargeScreen = useMediaQuery(
    () => `(min-width: ${MIN_WIDTH_SCREEN_TO_SHOW_CHAT}px)`
  );
  const showSlideChat = isChatVisible && isLargeScreen;
  const onGenerateText = async ({ type, index }) => {
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_TEXT_ACTION,
        showUsageConfirmationAlert: true,
      })
    );
    if (isBlocked) {
      return;
    }

    const currentTexts = getValues(`${BASIC_PATH}.${type}`) || [];
    const currentText = currentTexts[index]?.value || "";
    const allTexts = currentTexts.map((item) => item.value || "");
    const textsWithCurrentFirst = [
      currentText,
      ...allTexts.filter((_, i) => i !== index),
    ];

    const response = await dispatch(
      generateAdsCampaignDescription({
        objective,
        platform,
        campaignContext: getValues("context") || "",
        language: getValues(`language`),
        destinationUrl:
          getValues(`${BASIC_PATH}.destinationUrls`)?.[0]?.value || "",
        type,
        currentTexts: textsWithCurrentFirst,
      })
    );

    if (response) {
      setValue(`${BASIC_PATH}.${type}.[${index}].value`, response);
      trigger(`${BASIC_PATH}.${type}.[${index}].value`);
      onGeneratePreview();
    }
  };

  const onAddText = async ({ type }) => {
    const currentTexts = getValues(`${BASIC_PATH}.${type}`) || [];
    setValue(`${BASIC_PATH}.${type}`, [
      ...currentTexts,
      {
        value: "",
        id: getUniqueID(),
      },
    ]);
    onGeneratePreview();
  };
  const onRemoveText = async ({ type, index }) => {
    const currentTexts = getValues(`${BASIC_PATH}.${type}`);
    setValue(
      `${BASIC_PATH}.${type}`,
      currentTexts.filter((_, i) => i !== index)
    );
    trigger(`${BASIC_PATH}.${type}`);
  };

  const onGeneratePreview = () => {
    if (refPreview?.current) {
      refPreview.current?.generatePreview();
    }
  };

  const onChangeIsCarousel = async (value) => {
    setValue(`${BASIC_PATH}.isCarousel`, value);

    setValue(`${BASIC_PATH}.mediaUrls`, []);
    setValue(`${BASIC_PATH}.mediaData`, []);

    if (value === true) {
      setValue(
        `${BASIC_PATH}.musicUrl`,
        "https://clapp-prod.s3.amazonaws.com/Buzzly/soundtrack.mp3"
      );
      setValue(`${BASIC_PATH}.musicID`, null);
    }

    return true;
  };

  const onCopyValuesInOtherAds = ({ type }) => {
    const value = getValues(`${BASIC_PATH}.${type}`);
    const adsOnAdgroup = getValues(`${BASIC_PATH_AD_GROUP}.ads`) || [];

    adsOnAdgroup.forEach((ad, currentIndexAd) => {
      if (ad.id === adID) return;

      setValue(`${BASIC_PATH_AD_GROUP}.ads.[${currentIndexAd}].${type}`, value);
    });

    SimpleAlert({
      title: t(READY),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
  };

  //To reset values component when change selectedLevel
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await waitDelay(1);
      setIsLoading(false);
    })();
  }, [sectionSelected]);

  useEffect(() => {
    if (isCarousel === undefined || isCarousel === null || isCarousel === "") {
      setValue(`${BASIC_PATH}.isCarousel`, false);
    }
    //eslint-disable-next-line
  }, []);

  if (isLoading) return null;

  if (isCatalogCampaignTikTok) {
    return <AdCatalogTikTokForm />;
  }

  return (
    <>
      {isCarousel && (
        <AdsAdCarouselForm
          platform={platform}
          BASIC_PATH_AD_GROUP={BASIC_PATH_AD_GROUP}
          BASIC_PATH={BASIC_PATH}
          control={control}
          breakColumnFormAndPreview={breakColumnFormAndPreview}
          onChangeIsCarousel={onChangeIsCarousel}
          isDisabledFields={isDisabledFields}
          isAdCreated={isAdCreated}
          onAddText={onAddText}
          onRemoveText={onRemoveText}
          onGenerateText={onGenerateText}
          onCopyValuesInOtherAds={onCopyValuesInOtherAds}
          onGeneratePreview={onGeneratePreview}
          refPreview={refPreview}
          errors={errors}
          showSlideChat={showSlideChat}
        />
      )}
      {!isCarousel && (
        <AdsAdNormalForm
          platform={platform}
          BASIC_PATH_AD_GROUP={BASIC_PATH_AD_GROUP}
          BASIC_PATH={BASIC_PATH}
          control={control}
          errors={errors}
          refPreview={refPreview}
          breakColumnFormAndPreview={breakColumnFormAndPreview}
          onAddText={onAddText}
          onRemoveText={onRemoveText}
          onGenerateText={onGenerateText}
          onCopyValuesInOtherAds={onCopyValuesInOtherAds}
          onGeneratePreview={onGeneratePreview}
          onChangeIsCarousel={onChangeIsCarousel}
          isDisabledFields={isDisabledFields}
          isAdCreated={isAdCreated}
          showSlideChat={showSlideChat}
        />
      )}
    </>
  );
};

export default AdTikTokForm;

const AdsAdCarouselForm = ({
  platform,
  BASIC_PATH,
  control,
  breakColumnFormAndPreview,
  onChangeIsCarousel,
  isDisabledFields,
  onAddText,
  onRemoveText,
  onGenerateText,
  onCopyValuesInOtherAds,
  onGeneratePreview,
  BASIC_PATH_AD_GROUP,
  refPreview,
  errors,
  showSlideChat,
  isAdCreated,
}) => {
  const { watch, setValue } = useFormContext();
  const { t } = useTranslationApp();
  const platformData = watch(`platformsData.${platform}`);
  const objective = platformData?.objective;
  const isAppPromotionCampaign =
    objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION;
  const callToActionType = watch(`${BASIC_PATH}.callToActionType`);
  const titles = watch(`${BASIC_PATH}.titles`) || [{ value: "" }];
  const mediaUrls = watch(`${BASIC_PATH}.mediaUrls`) || [""];
  const adID = watch(`${BASIC_PATH}.id`);
  const mediaData = watch(`${BASIC_PATH}.mediaData`) || [{}];
  const isTikTokPost =
    mediaData?.[0]?.from === ADS_CAMPAIGN_MEDIA_FROM_TIKTOK_POST;
  const showTrackingID =
    !ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_TIKTOK.includes(objective);
  const musicUrl = watch(`${BASIC_PATH}.musicUrl`);

  useEffect(() => {
    if (!musicUrl) {
      setValue(
        `${BASIC_PATH}.musicUrl`,
        "https://clapp-prod.s3.amazonaws.com/Buzzly/soundtrack.mp3"
      );
    }
    //eslint-disable-next-line
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: breakColumnFormAndPreview ? "column" : "row",
        gap: 3,
        width: "100%",
        flexWrap: showSlideChat ? "wrap" : "nowrap",
      }}
    >
      <Grid container spacing={3} sx={{ height: "min-content" }}>
        {/* Creative Type Selector */}
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(CREATIVE_TYPE_PLACEHOLDER)}
                </Typography>
              </Box>
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Select
                    disabled={isDisabledFields}
                    variant="filled"
                    name={`${BASIC_PATH}.isCarousel`}
                    doTranslate={true}
                    actionsChange={(value) => {
                      onChangeIsCarousel(value);
                    }}
                    control={control}
                    options={[
                      { value: false, label: INDIVIDUAL },
                      { value: true, label: CAROUSEL },
                    ]}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContainer>
        </Grid>

        <Grid item xs={12}>
          <AdDarkPostForm
            basicPath={BASIC_PATH}
            disabled={isDisabledFields || isAdCreated}
          />
        </Grid>

        {/* Images Manager Card */}
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(CAROUSEL_IMAGES_MANAGER_TITLE, {
                    count: watch(`${BASIC_PATH}.mediaUrls`)?.length || 0,
                  })}
                </Typography>
              </Box>
            }
          >
            <CarouselImagesManager
              basicPath={BASIC_PATH}
              isDisabled={isDisabledFields}
              onGeneratePreview={(from) => onGeneratePreview(from)}
            />
          </CardContainer>
        </Grid>

        {/* Music Upload Card */}
        <Grid item xs={12}>
          <CardContainer>
            <MusicUploader
              name={`${BASIC_PATH}.musicUrl`}
              disabled={isDisabledFields}
              control={control}
              errors={errors}
            />
            <ClickableText
              sx={{ mt: 1 }}
              disabled={isDisabledFields}
              onClick={() => onCopyValuesInOtherAds({ type: "musicUrl" })}
            >
              {t(COPY_VALUES_IN_OTHER_ADS)}
            </ClickableText>
          </CardContainer>
        </Grid>

        {/* Card URL / Deep Link (igual a NormalForm) */}
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }} variant="h6">
                  {t(ADS_CONFIGURATION_DESTINATION_TITLE)}
                </Typography>
              </Box>
            }
          >
            <Grid container spacing={2}>
              {!isAppPromotionCampaign && (
                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography variant="body1">
                      {t(DESTINATION_URL_PLACEHOLDER)}
                    </Typography>
                    <TextField
                      name={`${BASIC_PATH}.destinationUrls.[0].value`}
                      variant="filled"
                      fullWidth
                      disabled={isDisabledFields}
                      control={control}
                      errors={errors}
                      rules={{
                        required: { value: false },
                        validate: (value) => {
                          if (!value) {
                            return t(FIELD_REQUIRED);
                          }
                          if (value && !REGEX_URL.test(encodeURI(value))) {
                            return t(FIELD_URL_VALID);
                          }
                        },
                      }}
                    />
                    <ClickableText
                      disabled={isDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "destinationUrls" })
                      }
                    >
                      {t(COPY_VALUE_IN_OTHER_ADS)}
                    </ClickableText>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">
                        {t(UTM_PARAMETERS_PLACEHOLDER)}
                      </Typography>
                      <ChipOptional size="small" />
                    </Box>
                    <UtmParametersContainer
                      name={`${BASIC_PATH}.utmParameters`}
                      control={control}
                      errors={errors}
                      disabled={isDisabledFields}
                      platform={platform}
                      onCopyValuesInOtherAds={() =>
                        onCopyValuesInOtherAds({ type: "utmParameters" })
                      }
                      copyText={COPY_VALUE_IN_OTHER_ADS}
                    />
                  </Box>
                </Grid>
              )}
              {isAppPromotionCampaign && (
                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">
                        {t(DEEP_LINK_PLACEHOLDER)}
                      </Typography>
                      <ChipOptional size="small" />
                    </Box>
                    <TextField
                      name={`${BASIC_PATH}.deepLink`}
                      variant="filled"
                      fullWidth
                      disabled={isDisabledFields}
                      control={control}
                      errors={errors}
                      rules={{
                        required: { value: false },
                        validate: (value) => {
                          if (!value) {
                            return t(FIELD_REQUIRED);
                          }
                          if (value && !REGEX_URL.test(encodeURI(value))) {
                            return t(FIELD_URL_VALID);
                          }
                        },
                      }}
                    />
                    <ClickableText
                      disabled={isDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "deepLink" })
                      }
                    >
                      {t(COPY_VALUES_IN_OTHER_ADS)}
                    </ClickableText>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContainer>
        </Grid>

        {/* Call to Action (igual a NormalForm) */}
        {objective !== ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }} variant="h6">
                    {t(
                      callToActionType === "multiple"
                        ? CALL_TO_ACTIONS_PLACEHOLDER
                        : CALL_TO_ACTION_PLACEHOLDER
                    )}
                  </Typography>
                </Box>
              }
            >
              <SelectCallToActionTikTok
                disabled={isDisabledFields}
                nameSingle={`${BASIC_PATH}.callToAction`}
                nameMultiple={`${BASIC_PATH}.callToActions`}
                mode={callToActionType}
                fullWidth
                objective={objective}
                platform={platform}
                doTranslate={true}
                control={control}
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                }}
              />
              <ClickableText
                disabled={isDisabledFields}
                onClick={() =>
                  onCopyValuesInOtherAds({
                    type:
                      callToActionType === "multiple"
                        ? "callToActions"
                        : "callToAction",
                  })
                }
              >
                {t(COPY_VALUES_IN_OTHER_ADS)}
              </ClickableText>
            </CardContainer>
          </Grid>
        )}

        {/* Títulos (igual a NormalForm) */}
        <Grid item xs={12} id={`${BASIC_PATH}.titles`}>
          <CardContainerWithCopy
            title={t(TITLE)}
            disabled={isDisabledFields}
            hasCopyValue={true}
            onCopyCallback={() => onCopyValuesInOtherAds({ type: "titles" })}
            id={`${BASIC_PATH}.titles`}
            copyText={COPY_VALUE_IN_OTHER_ADS}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {titles?.map((title, index) => (
                <TextFieldWithActions
                  key={title.id}
                  labelTitle={t(TITLE_PLACEHOLDER) + " " + (index + 1)}
                  type="titles"
                  showCounterCharacters={!isTikTokPost}
                  name={`${BASIC_PATH}.titles.[${index}].value`}
                  disabled={isDisabledFields || isTikTokPost}
                  showAddIcon={
                    index === titles.length - 1 && index < maxTitles - 1
                  }
                  showDeleteIcon={index !== 0}
                  platform={platform}
                  onChange={onGeneratePreview}
                  onAdd={() => onAddText({ type: "titles" })}
                  onRemove={() => onRemoveText({ type: "titles", index })}
                  maxLength={ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform]}
                  onGenerate={async () => {
                    await onGenerateText({ type: "titles", index });
                  }}
                  control={control}
                  errors={errors}
                  rules={{
                    required: {
                      value: true,
                      message: t(FIELD_REQUIRED),
                    },
                    validate: (value) => {
                      if (
                        value.length >
                        ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform]
                      ) {
                        return t(FIELD_MAX_LENGTH, {
                          value:
                            ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform],
                        });
                      }

                      const allTitles = titles.map((item) =>
                        (item.value || "").trim().toLowerCase()
                      );
                      const timesRepeated = allTitles.filter(
                        (text) => text === value.trim().toLowerCase()
                      ).length;
                      if (timesRepeated > 1) {
                        return t(FIELD_TEXT_REPEATED);
                      }
                      return true;
                    },
                  }}
                />
              ))}
            </Box>
          </CardContainerWithCopy>
        </Grid>

        {/* Tracking (igual a NormalForm) */}
        {showTrackingID && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t(TRACKING_EVENTS)}
                  </Typography>
                  <ChipOptional size="small" />
                </Box>
              }
            >
              <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1">
                      {t(LABELS_TRACKING_ID[platform])}
                    </Typography>
                    <SelectTrackingIDField
                      name={`${BASIC_PATH}.trackingID`}
                      disabled={isDisabledFields}
                      label={""}
                      fullWidth
                      variant="filled"
                      platform={platform}
                      integrationID={platformData?.integrationID}
                      adAccountID={platformData?.adAccountID}
                      control={control}
                      errors={errors}
                      rules={{
                        required: {
                          value: true,
                          message: t(FIELD_REQUIRED),
                        },
                      }}
                    />
                    <ClickableText
                      disabled={isDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "trackingID" })
                      }
                    >
                      {t(COPY_VALUES_IN_OTHER_ADS)}
                    </ClickableText>
                  </Box>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1">
                      {t(OFFLINE_EVENTS_PLACEHOLDER)}
                    </Typography>
                    <SelectOfflineEventsTikTok
                      name={`${BASIC_PATH}.offlineEvents`}
                      disabled={isDisabledFields}
                      label={""}
                      fullWidth
                      variant="filled"
                      platform={platform}
                      integrationID={platformData?.integrationID}
                      adAccountID={platformData?.adAccountID}
                      control={control}
                      errors={errors}
                      rules={{
                        required: {
                          value: true,
                          message: t(FIELD_REQUIRED),
                        },
                      }}
                    />
                    <ClickableText
                      disabled={isDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "offlineEvents" })
                      }
                    >
                      {t(COPY_VALUES_IN_OTHER_ADS)}
                    </ClickableText>
                  </Box>
                </Grid>
              </Grid>
            </CardContainer>
          </Grid>
        )}
      </Grid>
      {/* Preview */}
      <Box sx={{ width: "100%", maxWidth: 350 }}>
        <CardContainer
          dynamicHeight={true}
          title={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(ADS_CAMPAIGN_PREVIEW)}
              </Typography>
            </Box>
          }
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <AdPreviewTiktok
              ref={refPreview}
              adID={adID}
              mediaUrls={mediaUrls}
              mediaData={mediaData}
              titles={titles}
              platform={platform}
              basicPath={BASIC_PATH}
              basicPathAdGroup={BASIC_PATH_AD_GROUP}
            />
          </CardContent>
        </CardContainer>
      </Box>
    </Box>
  );
};

const AdsAdNormalForm = ({
  platform,
  BASIC_PATH_AD_GROUP,
  BASIC_PATH,
  control,
  errors,
  refPreview,
  breakColumnFormAndPreview,
  onAddText,
  onRemoveText,
  onGenerateText,
  onCopyValuesInOtherAds,
  onGeneratePreview,
  onChangeIsCarousel,
  isDisabledFields,
  showSlideChat,
  isAdCreated,
}) => {
  const { watch } = useFormContext();
  const { t } = useTranslationApp();
  const platformData = watch(`platformsData.${platform}`);
  const objective = platformData?.objective;
  const isAppPromotionCampaign =
    objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION;
  const callToActionType = watch(`${BASIC_PATH}.callToActionType`);
  const titles = watch(`${BASIC_PATH}.titles`) || [{ value: "" }];
  const mediaUrls = watch(`${BASIC_PATH}.mediaUrls`) || [""];
  const mediaData = watch(`${BASIC_PATH}.mediaData`) || [{}];
  const isTikTokPost =
    mediaData?.[0]?.from === ADS_CAMPAIGN_MEDIA_FROM_TIKTOK_POST;
  const adID = watch(`${BASIC_PATH}.id`);
  const showTrackingID =
    !ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_TIKTOK.includes(objective);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: breakColumnFormAndPreview ? "column" : "row",
        gap: 3,
        width: "100%",
        flexWrap: showSlideChat ? "wrap" : "nowrap",
      }}
    >
      {/* Form */}

      <Grid container spacing={3} sx={{ height: "min-content" }}>
        {/* Creative Type Selector */}
        {ADS_TIKTOK_CAMPAIGN_OBJECTIVES_VALID_WITH_IMAGES.includes(
          objective
        ) && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t(CREATIVE_TYPE_PLACEHOLDER)}
                  </Typography>
                </Box>
              }
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Select
                      disabled={isDisabledFields}
                      variant="filled"
                      name={`${BASIC_PATH}.isCarousel`}
                      doTranslate={true}
                      actionsChange={(value) => {
                        onChangeIsCarousel(value);
                      }}
                      control={control}
                      options={[
                        { value: false, label: INDIVIDUAL },
                        { value: true, label: CAROUSEL },
                      ]}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContainer>
          </Grid>
        )}

        <Grid item xs={12}>
          <AdDarkPostForm
            basicPath={BASIC_PATH}
            disabled={isDisabledFields || isAdCreated}
          />
        </Grid>

        {/* Card URL */}
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }} variant="h6">
                  {t(ADS_CONFIGURATION_DESTINATION_TITLE)}
                </Typography>
              </Box>
            }
          >
            <Grid container spacing={2}>
              {!isAppPromotionCampaign && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1">
                      {t(DESTINATION_URL_PLACEHOLDER)}
                    </Typography>
                    <TextField
                      name={`${BASIC_PATH}.destinationUrls.[0].value`}
                      variant="filled"
                      fullWidth
                      disabled={isDisabledFields}
                      control={control}
                      errors={errors}
                      rules={{
                        required: {
                          value: false,
                        },
                        validate: (value) => {
                          if (!value) {
                            return t(FIELD_REQUIRED);
                          }
                          if (value && !REGEX_URL.test(encodeURI(value))) {
                            return t(FIELD_URL_VALID);
                          }
                        },
                      }}
                    />
                    <ClickableText
                      disabled={isDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "destinationUrls" })
                      }
                    >
                      {t(COPY_VALUE_IN_OTHER_ADS)}
                    </ClickableText>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">
                        {t(UTM_PARAMETERS_PLACEHOLDER)}
                      </Typography>
                      <ChipOptional size="small" />
                    </Box>
                    <UtmParametersContainer
                      name={`${BASIC_PATH}.utmParameters`}
                      control={control}
                      errors={errors}
                      disabled={isDisabledFields}
                      platform={platform}
                      onCopyValuesInOtherAds={() =>
                        onCopyValuesInOtherAds({ type: "utmParameters" })
                      }
                      copyText={COPY_VALUE_IN_OTHER_ADS}
                    />
                  </Box>
                </Grid>
              )}
              {isAppPromotionCampaign && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">
                        {t(DEEP_LINK_PLACEHOLDER)}
                      </Typography>
                      <ChipOptional size="small" />
                    </Box>
                    <TextField
                      name={`${BASIC_PATH}.deepLink`}
                      variant="filled"
                      fullWidth
                      disabled={isDisabledFields}
                      control={control}
                      errors={errors}
                      rules={{
                        required: {
                          value: false,
                        },
                        validate: (value) => {
                          if (!value) {
                            return t(FIELD_REQUIRED);
                          }
                          if (value && !REGEX_URL.test(encodeURI(value))) {
                            return t(FIELD_URL_VALID);
                          }
                        },
                      }}
                    />
                    <ClickableText
                      disabled={isDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "deepLink" })
                      }
                    >
                      {t(COPY_VALUES_IN_OTHER_ADS)}
                    </ClickableText>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContainer>
        </Grid>

        {/* Call to Action */}
        {objective !== ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }} variant="h6">
                    {t(
                      callToActionType === "multiple"
                        ? CALL_TO_ACTIONS_PLACEHOLDER
                        : CALL_TO_ACTION_PLACEHOLDER
                    )}
                  </Typography>
                </Box>
              }
            >
              <SelectCallToActionTikTok
                disabled={isDisabledFields}
                nameSingle={`${BASIC_PATH}.callToAction`}
                nameMultiple={`${BASIC_PATH}.callToActions`}
                mode={callToActionType}
                fullWidth
                objective={objective}
                platform={platform}
                doTranslate={true}
                control={control}
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                }}
              />
              <ClickableText
                disabled={isDisabledFields}
                onClick={() =>
                  onCopyValuesInOtherAds({
                    type:
                      callToActionType === "multiple"
                        ? "callToActions"
                        : "callToAction",
                  })
                }
              >
                {t(COPY_VALUES_IN_OTHER_ADS)}
              </ClickableText>
            </CardContainer>
          </Grid>
        )}

        {/* Card Titles */}
        <Grid item xs={12} id={`${BASIC_PATH}.titles`}>
          <CardContainerWithCopy
            title={t(TITLE)}
            disabled={isDisabledFields}
            hasCopyValue={true}
            onCopyCallback={() => onCopyValuesInOtherAds({ type: "titles" })}
            id={`${BASIC_PATH}.titles`}
            copyText={COPY_VALUE_IN_OTHER_ADS}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {titles?.map((title, index) => (
                <TextFieldWithActions
                  key={title.id}
                  labelTitle={t(TITLE_PLACEHOLDER) + " " + (index + 1)}
                  type="titles"
                  showCounterCharacters={!isTikTokPost}
                  name={`${BASIC_PATH}.titles.[${index}].value`}
                  disabled={isDisabledFields || isTikTokPost}
                  showAddIcon={
                    index === titles.length - 1 && index < maxTitles - 1
                  }
                  showDeleteIcon={index !== 0}
                  platform={platform}
                  onChange={onGeneratePreview}
                  onAdd={() => onAddText({ type: "titles" })}
                  onRemove={() => onRemoveText({ type: "titles", index })}
                  maxLength={ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform]}
                  onGenerate={async () => {
                    await onGenerateText({ type: "titles", index });
                  }}
                  control={control}
                  errors={errors}
                  rules={{
                    required: {
                      value: true,
                      message: t(FIELD_REQUIRED),
                    },
                    validate: (value) => {
                      if (
                        value.length >
                        ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform]
                      ) {
                        return t(FIELD_MAX_LENGTH, {
                          value:
                            ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform],
                        });
                      }

                      const allTitles = titles.map((item) =>
                        (item.value || "").trim().toLowerCase()
                      );
                      const timesRepeated = allTitles.filter(
                        (text) => text === value.trim().toLowerCase()
                      ).length;
                      if (timesRepeated > 1) {
                        return t(FIELD_TEXT_REPEATED);
                      }
                      return true;
                    },
                  }}
                />
              ))}
            </Box>
          </CardContainerWithCopy>
        </Grid>

        {showTrackingID && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t(TRACKING_EVENTS)}
                  </Typography>
                  <ChipOptional size="small" />
                </Box>
              }
            >
              <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1">
                      {t(LABELS_TRACKING_ID[platform])}
                    </Typography>
                    <SelectTrackingIDField
                      name={`${BASIC_PATH}.trackingID`}
                      disabled={isDisabledFields}
                      label={""}
                      fullWidth
                      variant="filled"
                      platform={platform}
                      integrationID={platformData?.integrationID}
                      adAccountID={platformData?.adAccountID}
                      control={control}
                      errors={errors}
                      rules={{
                        required: {
                          value: true,
                          message: t(FIELD_REQUIRED),
                        },
                      }}
                    />
                    <ClickableText
                      disabled={isDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "trackingID" })
                      }
                    >
                      {t(COPY_VALUES_IN_OTHER_ADS)}
                    </ClickableText>
                  </Box>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1">
                      {t(OFFLINE_EVENTS_PLACEHOLDER)}
                    </Typography>
                    <SelectOfflineEventsTikTok
                      name={`${BASIC_PATH}.offlineEvents`}
                      disabled={isDisabledFields}
                      label={""}
                      fullWidth
                      variant="filled"
                      platform={platform}
                      integrationID={platformData?.integrationID}
                      adAccountID={platformData?.adAccountID}
                      control={control}
                      errors={errors}
                      rules={{
                        required: {
                          value: true,
                          message: t(FIELD_REQUIRED),
                        },
                      }}
                    />
                    <ClickableText
                      disabled={isDisabledFields}
                      onClick={() =>
                        onCopyValuesInOtherAds({ type: "offlineEvents" })
                      }
                    >
                      {t(COPY_VALUES_IN_OTHER_ADS)}
                    </ClickableText>
                  </Box>
                </Grid>
              </Grid>
            </CardContainer>
          </Grid>
        )}
      </Grid>

      {/* Preview */}
      <Box sx={{ width: "100%", maxWidth: 350 }}>
        <CardContainer
          dynamicHeight={true}
          title={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(ADS_CAMPAIGN_PREVIEW)}
              </Typography>
            </Box>
          }
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <AdPreviewTiktok
              ref={refPreview}
              adID={adID}
              mediaUrls={mediaUrls}
              mediaData={mediaData}
              titles={titles}
              platform={platform}
              basicPath={BASIC_PATH}
              basicPathAdGroup={BASIC_PATH_AD_GROUP}
            />
          </CardContent>
        </CardContainer>
      </Box>
    </Box>
  );
};

const AdDarkPostForm = ({ basicPath, disabled }) => {
  const { t } = useTranslationApp();
  const { watch, setValue } = useFormContext();
  const darkPost = watch(`${basicPath}.darkPost`) || "ON";
  return (
    <CardContainer
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t(DARK_POST_PLACEHOLDER)}
          </Typography>
        </Box>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Switch
          disabled={disabled}
          isActive={darkPost === "ON"}
          labelActive={DARK_POST_DESCRIPTION_PLACEHOLDER}
          labelDeactivate={DARK_POST_DESCRIPTION_PLACEHOLDER}
          onChangeIsActive={(value) => {
            setValue(`${basicPath}.darkPost`, value ? "ON" : "OFF");
          }}
          sxLabel={{
            ".MuiSwitch-root": {
              mt: -2,
            },
            ".MuiFormControlLabel-label": {
              fontSize: "0.9rem",
              fontWeight: 400,
            },
          }}
        />
      </Box>
    </CardContainer>
  );
};
